import { FC, useContext, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import classNames from 'classnames'

import { useAppDispatch, useAppSelector } from 'libraries/redux'
import { FacebookIcon, InstagramIcon, TelegramIcon, ViberIcon } from 'shared/assets/icons'
import { CategoriesSelectors } from 'store/category/selectors'
import { InformationSelectors } from 'store/information/selectors'
import { navigationList } from 'shared/lib/utils'

import styles from './Footer.module.scss'
import { SupplierContext } from 'context/SupplierContext'
import { useGetCustomerInformationListQuery } from '../../../entities/Customer/api/Informations/apiCustomerInfo'
import { getCategories } from 'store/category/actions'
import { MASTER_CARD_ICON, VISA_ICON } from 'shared/ui/img/Basket/payment-icon'

const Footer: FC = () => {
  const { data: storeInfo } = useAppSelector(CategoriesSelectors.storeInfo)
  const { data: informationList } = useAppSelector(InformationSelectors.informationList)
  const [email, setEmail] = useState('')

  ///For Suppliers
  const { is_supplier } = useContext(SupplierContext)
  const customerId = is_supplier ? Number(window.config.CUSTOMER_ID) : 0

  const { data: suppliersInformationList } = useGetCustomerInformationListQuery(
    {
      customerId: customerId,
      id_page: 1,
    },
    { skip: !is_supplier || !customerId }
  )

  const renderInformationList = is_supplier ? suppliersInformationList?.data : informationList

  const dispatch = useAppDispatch()

  useEffect(() => {
    if (!is_supplier) dispatch(getCategories(window.config.CATEGORY_ID))
  }, [dispatch, is_supplier])


  const socialUrls = [
    { Icon: ViberIcon, name: 'Viber', url: storeInfo?.viber_url },
    { Icon: TelegramIcon, name: 'Telegram', url: storeInfo?.telegram_url },
    { Icon: InstagramIcon, name: 'Instagram', url: storeInfo?.instagram_url },

  ].filter(item => item.url)

  return (
    <footer className={styles.wrapper}>
      <div className={classNames(styles.container)}>
        <div className={styles.footer_grid}>
          {/* Логотип */}
          <div className={styles.logo_column}>
            <div className={styles.logo_text}>{storeInfo?.name || 'FABU'}</div>
          </div>

          {/* Колонка 1: Компанія */}
          <div className={styles.footer_column}>
            <h3 className={styles.column_title}>Компанія</h3>
            <ul className={styles.links_list}>
              {navigationList.map(({ id, link, text, filter }) => (
                <li key={id}>
                  <Link to={link ?? `/products?filter=${filter}`}>
                    {text.toUpperCase()}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Колонка 2: Контакти */}
          <div className={styles.footer_column}>
            <h3 className={styles.column_title}>Контакти</h3>
            {storeInfo?.phone?.international && (
              <div className={styles.contact_info}>
                <a href={`tel:${storeInfo.phone.original}`} className={styles.phone_link}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clip-path="url(#clip0_7631_8537)">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M5.39381 2.4891C5.26161 2.19167 4.94919 2.01696 4.62657 2.06006L2.5665 2.33528C2.23165 2.38001 1.98906 2.67705 2.0121 3.01409C2.41458 8.90035 7.10029 13.5861 12.9866 13.9885C13.3236 14.0116 13.6206 13.769 13.6654 13.4341L13.9406 11.3741C13.9837 11.0514 13.809 10.739 13.5115 10.6068L11.4036 9.66997C11.2104 9.58411 10.9896 9.5858 10.7977 9.67462L9.97381 10.056C9.23803 10.3966 8.36788 10.2419 7.79455 9.66862L6.33202 8.20608C5.7587 7.63276 5.60402 6.76261 5.94463 6.02683L6.32602 5.20294C6.41484 5.01109 6.41653 4.79023 6.33067 4.59704L5.39381 2.4891ZM4.46767 0.87063C5.31821 0.757002 6.14187 1.2176 6.49038 2.00174L7.42724 4.10967C7.65361 4.619 7.64915 5.20125 7.415 5.70705L7.0336 6.53094C6.90441 6.81003 6.96308 7.14009 7.18055 7.35756L8.64308 8.82009C8.86055 9.03756 9.19061 9.09623 9.4697 8.96703L10.2936 8.58564C10.7994 8.35149 11.3816 8.34703 11.891 8.5734L13.9989 9.51026C14.783 9.85877 15.2436 10.6824 15.13 11.533L14.8548 13.593C14.7263 14.555 13.873 15.2519 12.9047 15.1857C6.4202 14.7424 1.25828 9.58043 0.814896 3.09595C0.748691 2.12768 1.44562 1.27436 2.4076 1.14585L4.46767 0.87063Z" fill="white"/>
                    </g>
                    <defs>
                      <clipPath id="clip0_7631_8537">
                        <rect width="16" height="16" fill="white"/>
                      </clipPath>
                    </defs>
                  </svg>

                  {storeInfo.phone.international}
                </a>
              </div>
            )}

            {socialUrls.length > 0 && (
              <div className={styles.social_section}>
                <div className={styles.socials}>
                  {socialUrls.map(({ Icon, name, url }) => (
                    <a
                      key={name}
                      href={url}
                      target='_blank'
                      rel='noopener noreferrer'
                      aria-label={name}
                      className={styles.social_link}
                      title={name}
                    >
                      <Icon  />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={styles.bottom_section}>
          {renderInformationList && renderInformationList.length > 0 && (
            <div className={styles.bottom_links}>
              {renderInformationList.map((info: any) => (
                <Link key={info.id} to={`/information/${info.id}`}>
                  {info.translation?.title}
                </Link>
              ))}
            </div>
          )}
          <span className={styles.copyright}>© 2025 {storeInfo?.name || 'FABU'}. Усі права захищено.</span>
        </div>
      </div>
    </footer>
  )
}

export default Footer
